import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { AssetAuditLog } from '@/types/models';

// Fetch audit logs with optional filters
export function useAuditLogs(
  organizationId: string,
  options?: {
    assetId?: string;
    userId?: string;
    startDate?: string;
    endDate?: string;
    actionType?: string;
    limit?: number;
    majorEventsOnly?: boolean;
  }
) {
  return useQuery({
    queryKey: ['auditLogs', organizationId, options],
    queryFn: async (): Promise<AssetAuditLog[]> => {
      // First, get the audit logs
      let query = supabase
        .from('asset_audit_log')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (options?.assetId) {
        query = query.eq('asset_id', options.assetId);
      }

      if (options?.userId) {
        query = query.eq('user_id', options.userId);
      }

      if (options?.actionType) {
        query = query.eq('action_type', options.actionType);
      }

      if (options?.startDate) {
        query = query.gte('created_at', options.startDate);
      }

      if (options?.endDate) {
        query = query.lte('created_at', options.endDate);
      }

      if (options?.majorEventsOnly) {
        query = query.eq('is_major_event', true);
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Get unique user IDs and fetch their profiles
      const userIds = [...new Set((data || []).map((log: any) => log.user_id).filter(Boolean))];
      const profileMap = new Map<string, string>();

      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', userIds);

        profiles?.forEach((p: any) => {
          profileMap.set(p.id, p.full_name || 'Unknown User');
        });
      }

      // Map profile data to user_name field
      return (data || []).map((log: any) => ({
        ...log,
        user_name: log.user_id ? (profileMap.get(log.user_id) || 'Unknown User') : 'System',
      }));
    },
    enabled: !!organizationId,
  });
}

// Fetch audit logs for a specific asset
export function useAssetAuditLogs(organizationId: string, assetId: string) {
  return useQuery({
    queryKey: ['assetAuditLogs', organizationId, assetId],
    queryFn: async (): Promise<AssetAuditLog[]> => {
      const { data, error } = await supabase
        .from('asset_audit_log')
        .select(`
          *,
          asset:assets(description, tag_id)
        `)
        .eq('organization_id', organizationId)
        .eq('asset_id', assetId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get unique user IDs and fetch their profiles
      const userIds = [...new Set((data || []).map((log: any) => log.user_id).filter(Boolean))];
      const profileMap = new Map<string, string>();

      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', userIds);

        profiles?.forEach((p: any) => {
          profileMap.set(p.id, p.full_name || 'Unknown User');
        });
      }

      return (data || []).map((log: any) => ({
        ...log,
        user_name: log.user_id ? (profileMap.get(log.user_id) || 'Unknown User') : 'System',
      }));
    },
    enabled: !!organizationId && !!assetId,
  });
}

// Fetch audit logs for a specific user
export function useUserAuditLogs(organizationId: string, userId: string, limit = 50) {
  return useQuery({
    queryKey: ['userAuditLogs', organizationId, userId, limit],
    queryFn: async (): Promise<AssetAuditLog[]> => {
      const { data, error } = await supabase
        .from('asset_audit_log')
        .select(`
          *,
          asset:assets(description, tag_id)
        `)
        .eq('organization_id', organizationId)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Get the user's profile (we already know the userId)
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', userId)
        .single();

      const userName = profile?.full_name || 'Unknown User';

      return (data || []).map((log: any) => ({
        ...log,
        user_name: userName,
      }));
    },
    enabled: !!organizationId && !!userId,
  });
}

// Fetch audit logs for a date range
export function useDateRangeAuditLogs(
  organizationId: string,
  startDate: string,
  endDate: string
) {
  return useQuery({
    queryKey: ['dateRangeAuditLogs', organizationId, startDate, endDate],
    queryFn: async (): Promise<AssetAuditLog[]> => {
      const { data, error } = await supabase
        .from('asset_audit_log')
        .select(`
          *,
          asset:assets(description, tag_id)
        `)
        .eq('organization_id', organizationId)
        .gte('created_at', startDate)
        .lte('created_at', endDate)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get unique user IDs and fetch their profiles
      const userIds = [...new Set((data || []).map((log: any) => log.user_id).filter(Boolean))];
      const profileMap = new Map<string, string>();

      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', userIds);

        profiles?.forEach((p: any) => {
          profileMap.set(p.id, p.full_name || 'Unknown User');
        });
      }

      return (data || []).map((log: any) => ({
        ...log,
        user_name: log.user_id ? (profileMap.get(log.user_id) || 'Unknown User') : 'System',
      }));
    },
    enabled: !!organizationId && !!startDate && !!endDate,
  });
}

// Get recent activity (major events only)
export function useRecentActivity(organizationId: string, limit = 20) {
  return useQuery({
    queryKey: ['recentActivity', organizationId, limit],
    queryFn: async (): Promise<AssetAuditLog[]> => {
      const { data, error } = await supabase
        .from('asset_audit_log')
        .select(`
          *,
          asset:assets(description, tag_id)
        `)
        .eq('organization_id', organizationId)
        .eq('is_major_event', true)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Get unique user IDs and fetch their profiles
      const userIds = [...new Set((data || []).map((log: any) => log.user_id).filter(Boolean))];
      const profileMap = new Map<string, string>();

      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', userIds);

        profiles?.forEach((p: any) => {
          profileMap.set(p.id, p.full_name || 'Unknown User');
        });
      }

      return (data || []).map((log: any) => ({
        ...log,
        user_name: log.user_id ? (profileMap.get(log.user_id) || 'Unknown User') : 'System',
      }));
    },
    enabled: !!organizationId,
  });
}

// Manual log creation (for cases not covered by triggers)
export function useCreateAuditLog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (log: {
      organization_id: string;
      asset_id?: string;
      action_type: string;
      event_category: string;
      table_name: string;
      field_changed?: string;
      old_value?: string;
      new_value?: string;
      change_summary: string;
      metadata?: Record<string, any>;
      is_major_event?: boolean;
    }) => {
      const { data, error } = await supabase.rpc('log_asset_change', {
        p_organization_id: log.organization_id,
        p_asset_id: log.asset_id || null,
        p_action_type: log.action_type,
        p_event_category: log.event_category,
        p_table_name: log.table_name,
        p_field_changed: log.field_changed || null,
        p_old_value: log.old_value || null,
        p_new_value: log.new_value || null,
        p_change_summary: log.change_summary,
        p_metadata: log.metadata || {},
        p_is_major_event: log.is_major_event || false,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
      queryClient.invalidateQueries({ queryKey: ['assetAuditLogs'] });
      queryClient.invalidateQueries({ queryKey: ['userAuditLogs'] });
      queryClient.invalidateQueries({ queryKey: ['recentActivity'] });
    },
  });
}

// Add a note to an asset (convenience function)
export function useAddAssetNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      assetId,
      note,
    }: {
      organizationId: string;
      assetId: string;
      note: string;
    }) => {
      const { data, error } = await supabase.rpc('add_asset_note', {
        p_organization_id: organizationId,
        p_asset_id: assetId,
        p_note: note,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['assetAuditLogs', variables.organizationId, variables.assetId] });
      queryClient.invalidateQueries({ queryKey: ['assetNotes', variables.organizationId, variables.assetId] });
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
      queryClient.invalidateQueries({ queryKey: ['recentActivity'] });
    },
  });
}

// Get only notes for an asset
export function useAssetNotes(organizationId: string, assetId: string) {
  return useQuery({
    queryKey: ['assetNotes', organizationId, assetId],
    queryFn: async (): Promise<AssetAuditLog[]> => {
      const { data, error } = await supabase
        .from('asset_audit_log')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('asset_id', assetId)
        .eq('action_type', 'NOTE')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get unique user IDs and fetch their profiles
      const userIds = [...new Set((data || []).map((log: any) => log.user_id).filter(Boolean))];
      const profileMap = new Map<string, string>();

      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', userIds);

        profiles?.forEach((p: any) => {
          profileMap.set(p.id, p.full_name || 'Unknown User');
        });
      }

      return (data || []).map((log: any) => ({
        ...log,
        user_name: log.user_id ? (profileMap.get(log.user_id) || 'Unknown User') : 'System',
      }));
    },
    enabled: !!organizationId && !!assetId,
  });
}
